import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { NgFor, NgIf } from '@angular/common';
import {
    MatTable,
    MatTableModule,
    MatTableDataSource,
} from '@angular/material/table';
import {
    FormsModule,
    ReactiveFormsModule,
    FormBuilder,
    FormGroup,
    Validators,
    ValidatorFn,
    AbstractControl,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { SelectionModel } from '@angular/cdk/collections';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { CustomizerSettingsService } from '../../customizer-settings/customizer-settings.service';
import { TranslocoModule } from '@ngneat/transloco';
import { ApiService } from '../../services/api/api.service';
import { Role } from '../../interfaces/Role';
import { RoleResponse } from '../../interfaces/RoleResponse';
import { Company } from '../../interfaces/Company';
import { CompanyResponse } from '../../interfaces/CompanyResponse';
import { BranchOffice } from './../../interfaces/BranchOffice';
import { BranchOfficeResponse } from './../../interfaces/BranchOfficeResponse';
import { CompanyUserContactInfo } from '../../interfaces/CompanyUserContactInfo';
import { ContactMediumType } from '../../interfaces/ContactMediumType';
import { ContactMediumTypeResponse } from '../../interfaces/ContactMediumTypeResponse';
import { CompanyUser } from '../../interfaces/CompanyUser';
import { CompanyUserResponse } from '../../interfaces/CompanyUserResponse';
import { MatDialog } from '@angular/material/dialog';
import { AlertDialogComponent } from '../../common/alert-dialog/alert-dialog.component';

@Component({
    selector: 'app-m-edit-company-user',
    standalone: true,
    imports: [
        MatTableModule,
        MatCardModule,
        MatMenuModule,
        MatButtonModule,
        RouterLink,
        FormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatCheckboxModule,
        MatSelectModule,
        MatDatepickerModule,
        MatNativeDateModule,
        ReactiveFormsModule,
        TranslocoModule,
        NgFor,
        NgIf,
    ],
    templateUrl: './m-edit-company-user.component.html',
    styleUrl: './m-edit-company-user.component.scss',
})
export class MEditCompanyUserComponent implements OnInit {
    // Id
    id: string;

    // Company Id
    companyId: string;

    // Company User
    editCompanyUser: CompanyUser;

    // isToggled
    isToggled = false;

    // Forms
    editCompanyUserForm: FormGroup;
    addContactForm: FormGroup;

    // Password Hide
    hide = true;
    hide2 = true;
    passwordsMatching = false;
    isConfirmPasswordDirty = false;

    // Array of roles
    roles: Role[] = [];

    selectedRole: Role | undefined;

    // Array of companies
    companies: Company[] = [];

    selectedCompany: string = '';

    // Array of branch offices
    branchOffices: BranchOffice[] = [];

    selectedBranchOffice: BranchOffice | undefined;

    // Array of contact medium types
    contactMediumTypes: ContactMediumType[] = [];

    selectedContactMediumType: ContactMediumType | undefined;

    displayedColumns: string[] = ['select', 'contactMediumName', 'contact'];
    contactInfos: CompanyUserContactInfo[] = [];
    dataSource = new MatTableDataSource<CompanyUserContactInfo>(
        this.contactInfos
    );
    selection = new SelectionModel<CompanyUserContactInfo>(true, []);

    // Show alert
    showAlert: boolean = false;

    readonly dialog = inject(MatDialog);

    @ViewChild(MatTable) table: MatTable<CompanyUserContactInfo>;

    constructor(
        private fb: FormBuilder,
        public themeService: CustomizerSettingsService,
        private apiService: ApiService,
        private router: Router,
        private route: ActivatedRoute
    ) {
        this.editCompanyUserForm = this.fb.group(
            {
                name: ['', [Validators.required, Validators.maxLength(300)]],
                email: [
                    '',
                    [
                        Validators.required,
                        Validators.maxLength(300),
                        Validators.email,
                    ],
                ],
                password: [
                    '',
                    [
                        Validators.minLength(8),
                        Validators.maxLength(50),
                        Validators.pattern(
                            /(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[!-\/:-@[-`{-~]).{8,50}/
                        ),
                    ],
                ],
                confirmPassword: [
                    '',
                    [
                        Validators.minLength(8),
                        Validators.maxLength(50),
                        Validators.pattern(
                            /(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[!-\/:-@[-`{-~]).{8,50}/
                        ),
                    ],
                ],
                role: [{}, [Validators.required]],
                branchOffice: [{}, [Validators.required]],
            },
            {
                validator: this.matchValidator('password', 'confirmPassword'),
            }
        );
        this.addContactForm = this.fb.group({
            contact: ['', [Validators.required, Validators.maxLength(200)]],
        });
        this.themeService.isToggled$.subscribe((isToggled) => {
            this.isToggled = isToggled;
        });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    ngOnInit() {
        this.apiService
            .getCompanyPagination(0, 1000000, 'name', 'asc')
            .subscribe((response: CompanyResponse) => {
                this.companies.push(...response.companies);
            });
        this.apiService
            .getContactMediumType()
            .subscribe((response: ContactMediumTypeResponse) => {
                this.contactMediumTypes.push(...response.contactMediumTypes);
            });

        this.route.params.subscribe((params) => {
            this.id = params['id'];
            this.companyId = params['companyId'];

            this.apiService
                .getCompanyUserByIdAndCompanyId(this.id, this.companyId)
                .subscribe((data: CompanyUserResponse) => {
                    if (data.companyUser) {
                        this.editCompanyUser = data.companyUser;
                        console.log(this.editCompanyUser);
                        this.apiService
                            .getRole()
                            .subscribe((response: RoleResponse) => {
                                this.roles.push(...response.roles);
                                this.selectedRole = this.roles.find(
                                    ({ id }) =>
                                        id === this.editCompanyUser.role.id
                                );
                            });

                        this.selectedCompany =
                            this.editCompanyUser.branchOffice.companyId;

                        this.apiService
                            .getBranchOfficeByCompanyId(
                                this.selectedCompany,
                                0,
                                1000000,
                                'name',
                                'asc'
                            )
                            .subscribe((response: BranchOfficeResponse) => {
                                this.branchOffices.push(
                                    ...response.branchOffices
                                );
                                this.selectedBranchOffice =
                                    this.branchOffices.find(
                                        ({ id }) =>
                                            id ===
                                            this.editCompanyUser.branchOffice.id
                                    );
                            });
                    }
                    if (
                        this.editCompanyUser.contactInfos &&
                        this.editCompanyUser.contactInfos.length > 0
                    ) {
                        this.contactInfos.push(
                            ...this.editCompanyUser.contactInfos
                        );
                    }
                });
        });
    }

    // RTL Mode
    toggleRTLEnabledTheme() {
        this.themeService.toggleRTLEnabledTheme();
    }

    /** Whether the number of selected elements matches the total number of rows. */
    isAllSelected() {
        const numSelected = this.selection.selected.length;
        const numRows = this.dataSource.data.length;
        return numSelected === numRows;
    }

    /** Selects all rows if they are not all selected; otherwise clear selection. */
    toggleAllRows() {
        if (this.isAllSelected()) {
            this.selection.clear();
            return;
        }
        this.selection.select(...this.dataSource.data);
    }

    /**
     * removeSelectedRows
     */
    removeSelectedRows() {
        this.selection.selected.forEach((item) => {
            let index: number = this.contactInfos.findIndex((d) => d === item);
            console.log(this.contactInfos.findIndex((d) => d === item));
            this.contactInfos.splice(index, 1);
            this.dataSource = new MatTableDataSource<CompanyUserContactInfo>(
                this.contactInfos
            );
        });
        this.selection = new SelectionModel<CompanyUserContactInfo>(true, []);
    }

    // Change company
    changeCompany(companyId: string) {
        this.branchOffices = [];
        this.apiService
            .getBranchOfficeByCompanyId(companyId, 0, 1000000, 'name', 'asc')
            .subscribe((response: BranchOfficeResponse) => {
                this.branchOffices.push(...response.branchOffices);
            });
    }

    /**
     * On submit
     */
    onSubmit(): void {
        // Return if the form is invalid
        if (this.editCompanyUserForm.invalid || this.classApplied) {
            this.showAlert = false;
            return;
        }

        // Disable the form
        this.editCompanyUserForm.disable();

        // Hide the alert
        this.showAlert = false;

        // Edit Company User
        if (this.editCompanyUserForm.get('password')?.value) {
            this.editCompanyUser.password =
                this.editCompanyUserForm.get('password')?.value;
        }

        this.editCompanyUser.role = this.editCompanyUserForm.get('role')?.value;
        this.editCompanyUser.branchOffice =
            this.editCompanyUserForm.get('branchOffice')?.value;

        this.editCompanyUser.contactInfos = this.contactInfos;
        console.log(this.editCompanyUser);
        this.apiService
            .patchCompanyUser(this.id, this.editCompanyUser)
            .subscribe({
                next: (value) => {
                    console.info(value);
                    // Re-enable the form
                    this.editCompanyUserForm.enable();

                    // Show the alert
                    this.showAlert = true;
                },
                error: (error) => {
                    let errMsg: string = error;
                    const dialogRef = this.dialog.open(AlertDialogComponent, {
                        data: {
                            title: 'ERROR',
                            message: error,
                        },
                    });
                    // Re-enable the form
                    this.editCompanyUserForm.enable();

                    // Show the alert
                    this.showAlert = true;
                },
                complete: () => {
                    this.router.navigateByUrl('/masters/company-users');
                },
            });
    }

    // Popup Trigger
    classApplied = false;
    toggleClass() {
        this.classApplied = !this.classApplied;
    }

    // Match Validator
    matchValidator(
        controlName: string,
        matchingControlName: string
    ): ValidatorFn {
        return (abstractControl: AbstractControl) => {
            const control = abstractControl.get(controlName);
            const matchingControl = abstractControl.get(matchingControlName);

            if (
                matchingControl!.errors &&
                !matchingControl!.errors?.['confirmedValidator']
            ) {
                return null;
            }

            if (control!.value !== matchingControl!.value) {
                const error = { confirmedValidator: 'Passwords do not match.' };
                matchingControl!.setErrors(error);
                return error;
            } else {
                matchingControl!.setErrors(null);
                return null;
            }
        };
    }

    // Add Contact Info
    addContactInfo() {
        const newCompanyContactInfo: CompanyUserContactInfo = {
            ...this.addContactForm.value,
            contactMediumType: this.selectedContactMediumType,
        };
        this.contactInfos.push(newCompanyContactInfo);
        this.table.renderRows();
        this.selectedContactMediumType = undefined;
        this.addContactForm.reset();
        this.classApplied = !this.classApplied;
    }
}
